import React, { useCallback, useState } from 'react';
import useFileUpload from '../../hooks/useFileUpload';
import Button from '../Button/Button';
import { Box } from 'rebass';
import { useMutation } from '@apollo/client';
import useNotify from '../../hooks/useNotify';
import { useUploadTempFile } from '../../hooks/useUploadTempFile';
import { gql } from '@/modes/waterSupplier/gql';

const ADD_ASSEMBLY_ATTACHMENT = gql(`
  mutation AddBackflowAssemblyAttachment(
    $assemblyId: ID!
    $tempKey: String!
    $contentType: String!
  ) {
    addBackflowAssemblyAttachment(
      assemblyId: $assemblyId
      tempKey: $tempKey
      contentType: $contentType
    ) {
      id
    }
  }
`);

type AssemblyPhotosEditProps = {
  assemblyId: string;
  onPhotosAdded?: (() => void) | (() => Promise<any>);
};

const accept = {
  'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
  'application/pdf': ['.pdf'],
};

const AssemblyPhotosEdit: React.FC<AssemblyPhotosEditProps> = ({
  assemblyId,
  onPhotosAdded,
}) => {
  const { showError } = useNotify();

  const [addAssemblyAttachment] = useMutation<
    { addBackflowAssemblyAttachment: { id: string } },
    { assemblyId: string; tempKey: string; contentType: string }
  >(ADD_ASSEMBLY_ATTACHMENT, {
    onError: (error) => {
      showError('Error attaching photo to assembly', error);
    },
  });

  const [uploading, setUploading] = useState<boolean>(false);

  const { uploadTempFiles } = useUploadTempFile();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        setUploading(true);
        const uploadResults = await uploadTempFiles(acceptedFiles);

        const promises = uploadResults.map((tempFile) => {
          return addAssemblyAttachment({
            variables: {
              assemblyId,
              contentType: tempFile.contentType,
              tempKey: tempFile.key,
            },
          });
        });

        await Promise.all(promises);
        onPhotosAdded?.();
      } catch (e) {
        showError('Error uploading files', e as Error);
      } finally {
        setUploading(false);
      }
    },
    [
      uploadTempFiles,
      assemblyId,
      addAssemblyAttachment,
      onPhotosAdded,
      showError,
    ]
  );

  const { getRootProps, getInputProps } = useFileUpload({
    multiple: true,
    accept,
    onDrop,
    disabled: uploading,
  });

  return (
    <>
      <Box flexShrink={0} flexGrow={0} {...(getRootProps() as any)}>
        <input {...getInputProps()} />
        <Button icon="image" variant="secondary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Select...'}
        </Button>
      </Box>
    </>
  );
};

export default AssemblyPhotosEdit;
